/**
 * AdminActivity — High King-only feed of user activity across the
 * platform. Shows aggregate cards (logins / signups / appointments /
 * payments etc.) for the last N days, plus a live event feed.
 *
 * Feb 12 2026 — user spec: "track user activities in the app and show
 * in admin analytics".
 *
 * Hits:
 *   GET /api/admin/analytics/summary?days=
 *   GET /api/admin/analytics/activity?limit=200
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ArrowLeft, Loader2, Activity, Globe } from 'lucide-react';
import { checkAuth, authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_COLORS = {
  login:               'text-cyan-300 bg-cyan-500/10 border-cyan-500/30',
  register:            'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  king_appointed:      'text-amber-300 bg-amber-500/10 border-amber-500/30',
  ambassador_appointed:'text-pink-300 bg-pink-500/10 border-pink-500/30',
  content_open:        'text-indigo-300 bg-indigo-500/10 border-indigo-500/30',
  episode_read:        'text-indigo-300 bg-indigo-500/10 border-indigo-500/30',
  payment:             'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  writer_application:  'text-purple-300 bg-purple-500/10 border-purple-500/30',
};

const KindBadge = ({ kind }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${KIND_COLORS[kind] || 'text-slate-300 bg-slate-500/10 border-slate-500/30'}`}>
    {kind?.replaceAll('_', ' ')}
  </span>
);

const AdminActivity = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, fRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/admin/analytics/summary?days=${days}`),
        authFetch(`${BACKEND_URL}/api/admin/analytics/activity?limit=200`),
      ]);
      if (sRes.ok) setSummary(await sRes.json());
      if (fRes.ok) setFeed((await fRes.json()).items || []);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (cancelled) return;
      if (!u || u.role !== 'admin') {
        navigate('/admin-login');
        return;
      }
      await refresh();
    })();
    return () => { cancelled = true; };
  }, [navigate, refresh]);

  return (
    <main className="min-h-screen max-w-5xl mx-auto px-4 py-6 pb-24 space-y-5" data-testid="admin-activity-page">
      <header className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-cyan-200/80 hover:text-cyan-100"
          data-testid="admin-activity-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">User Activity</h1>
        </div>
        <div className="flex items-center gap-2">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${
                days === d
                  ? 'bg-cyan-500/30 border-cyan-400/50 text-white'
                  : 'border-slate-700 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-200'
              }`}
              data-testid={`admin-activity-days-${d}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>
      </header>

      {loading && !summary ? (
        <div className="text-center text-slate-400 text-sm py-8">
          <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading activity…
        </div>
      ) : (
        <>
          {/* Top-line cards */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-activity-summary">
            <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4">
              <p className="text-[10px] uppercase tracking-widest text-cyan-200/80 font-bold">Active users</p>
              <p className="text-3xl font-black text-white tabular-nums mt-1" data-testid="admin-activity-active-users">
                {summary?.active_users ?? 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">last {summary?.window_days || days}d</p>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <p className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-bold">Total events</p>
              <p className="text-3xl font-black text-white tabular-nums mt-1" data-testid="admin-activity-total-events">
                {summary?.total_events ?? 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">tracked</p>
            </div>
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">Logins</p>
              <p className="text-3xl font-black text-white tabular-nums mt-1">
                {summary?.by_kind?.login ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border border-pink-500/30 bg-pink-500/10 p-4">
              <p className="text-[10px] uppercase tracking-widest text-pink-200/80 font-bold">Signups</p>
              <p className="text-3xl font-black text-white tabular-nums mt-1">
                {summary?.by_kind?.register ?? 0}
              </p>
            </div>
          </section>

          {/* Country breakdown */}
          {!!summary?.by_country?.length && (
            <section
              className="rounded-2xl border border-slate-700 bg-slate-950/40 p-4 space-y-2"
              data-testid="admin-activity-countries"
            >
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-300" />
                <h2 className="text-sm font-bold text-white">Activity by country</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {summary.by_country.map((c) => (
                  <div
                    key={c.country}
                    className="rounded-lg border border-slate-700 bg-slate-900/40 p-2 flex items-center justify-between"
                    data-testid={`admin-activity-country-${c.country}`}
                  >
                    <span className="text-xs font-bold text-white">{c.country}</span>
                    <span className="text-xs text-slate-400 tabular-nums">{c.count}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Live feed */}
          <section
            className="rounded-2xl border border-slate-700 bg-slate-950/40 p-4 space-y-2"
            data-testid="admin-activity-feed"
          >
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-300" />
              <h2 className="text-sm font-bold text-white">Live feed</h2>
              <span className="text-[11px] text-slate-500 tabular-nums">{feed.length} events</span>
            </div>
            {feed.length === 0 ? (
              <p className="text-sm text-slate-400 py-3">No activity yet. New events will land here as people use the app.</p>
            ) : (
              <ol className="divide-y divide-slate-800">
                {feed.map((row, i) => (
                  <li
                    key={`${row.user_id}-${row.created_at}-${i}`}
                    className="py-2 flex items-start gap-3"
                    data-testid={`admin-activity-row-${i}`}
                  >
                    <KindBadge kind={row.kind} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        <span className="font-bold">{row.user_name || row.user_id}</span>{' '}
                        <span className="text-slate-400">— {row.label}</span>
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {row.country ? `${row.country} · ` : ''}
                        {row.role ? `${row.role} · ` : ''}
                        {new Date(row.created_at).toLocaleString()}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </main>
  );
};

export default AdminActivity;
